import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { TvShow } from '@/types/tv';
import ScoreBadge from '@/components/feature/ScoreBadge';

interface HeroCarouselProps {
  shows: TvShow[];
}

export default function HeroCarousel({ shows }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timerRef = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      if (shows.length === 0) return;
      setActive((next + shows.length) % shows.length);
    },
    [shows.length],
  );

  useEffect(() => {
    if (shows.length <= 1) return;
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % shows.length);
    }, 5500);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [shows.length]);

  if (shows.length === 0) {
    return (
      <div className="h-[300px] md:h-[420px] flex items-center justify-center bg-background-100 text-foreground-500">
        暂无推荐
      </div>
    );
  }

  const current = shows[active];
  const image = current.heroImage || current.poster;

  return (
    <section className="relative w-full h-[440px] md:h-[580px] overflow-hidden bg-primary-950">
      <img
        key={current.id}
        src={image}
        alt={`${current.title}焦点剧照`}
        title={`${current.title} - 编辑推荐高分剧`}
        className="absolute inset-0 w-full h-full object-cover object-top animate-fade-in"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>

      <div className="relative h-full flex items-center px-4 md:px-6">
        <div className="max-w-2xl animate-fade-up" key={`content-${current.id}`}>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
              编辑推荐
            </span>
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-50/15 text-background-100 backdrop-blur whitespace-nowrap">
              {current.region} · {current.year}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
            {current.title}
          </h1>
          <div className="flex items-center gap-3 mt-4">
            <ScoreBadge score={current.score} size="md" />
            <span className="text-sm text-background-200">
              {current.tags.join(' · ')} · 共 {current.episodes} 集
            </span>
          </div>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-background-100/90 line-clamp-3 max-w-xl">
            {current.intro} {current.synopsis[0]}
          </p>
          <div className="flex items-center gap-3 mt-7">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.categories[0]}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold backdrop-blur hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              同类推荐
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 right-5 md:right-6 flex items-center gap-3">
        <div className="flex items-center gap-2">
          {shows.map((show, index) => (
            <button
              key={show.id}
              type="button"
              aria-label={`切换到 ${show.title}`}
              onClick={() => go(index)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                index === active ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
              }`}
            ></button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(active - 1)}
            className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur hover:bg-background-50/30 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(active + 1)}
            className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur hover:bg-background-50/30 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}